import React from 'react'
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, Loader2Icon } from "lucide-react"
import { useAutoAnimate } from '@formkit/auto-animate/react'
import CardContentOverlay from '@/components/CardContentOverlay'
import { SettingsCardContext, SettingsPageContext } from './utils'


type SettingsCardShellProps = {
    cardCtx: SettingsCardContext;
    pageCtx: SettingsPageContext;
    advancedVisible?: boolean;
    advancedSetter?: (visible: boolean) => void;
    onClickSave: () => void;
    children: React.ReactNode;
};

export default function SettingsCardShell({
    cardCtx,
    pageCtx,
    advancedVisible,
    advancedSetter,
    onClickSave,
    children,
}: SettingsCardShellProps) {
    const [animationParent] = useAutoAnimate();
    const isCardPendingSave = pageCtx.cardPendingSave?.cardId === cardCtx.cardId;

    return (
        <div id={`tab-${cardCtx.cardId}`} data-show-advanced={advancedVisible} className='group/card'>
            <div className="rounded-xl overflow-hidden" style={{
                background: 'linear-gradient(180deg, #0e1525 0%, #090d18 100%)',
                border: '1px solid rgba(0,255,209,0.12)',
                boxShadow: '0 0 0 1px rgba(0,255,209,0.04), 0 4px 24px rgba(0,0,0,0.4)',
            }}>
                {/* Card header breadcrumb */}
                <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2"
                    style={{ borderColor: 'rgba(0,255,209,0.10)', background: 'rgba(0,255,209,0.04)' }}>
                    <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: 'var(--sc-muted)' }}>
                        {cardCtx.tabName !== cardCtx.cardName ? (
                            <>
                                <span>{cardCtx.tabName}</span>
                                <ChevronRightIcon className="size-3 opacity-50" />
                                <span style={{ color: 'var(--sc-text)' }}>{cardCtx.cardName} Settings</span>
                            </>
                        ) : (
                            <span style={{ color: 'var(--sc-text)' }}>{cardCtx.tabName} Settings</span>
                        )}
                        {isCardPendingSave && (
                            <span className="ml-2 text-[10px] font-medium italic" style={{ color: 'var(--sc-warn)' }}>
                                · unsaved changes
                            </span>
                        )}
                    </div>
                </div>

                {/* Card content */}
                <div className='relative'>
                    <div className="space-y-6 p-5 overflow-x-clip" ref={animationParent}>
                        {children}

                        {/* Save / Advanced buttons */}
                        <div className="flex flex-wrap-reverse justify-start gap-2 pt-2 border-t"
                            style={{ borderColor: 'var(--sc-border)' }}>
                            <button
                                disabled={!isCardPendingSave || pageCtx.isReadOnly}
                                onClick={onClickSave}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                                style={{
                                    background: 'rgba(0,255,209,0.10)',
                                    color: 'var(--sc-cyan)',
                                    border: '1px solid var(--sc-border-mid)',
                                }}
                            >
                                {pageCtx.isSaving
                                    ? <><Loader2Icon className="h-3.5 animate-spin" /> Saving...</>
                                    : <>Save {cardCtx.cardName} Settings</>
                                }
                            </button>
                            {advancedVisible !== undefined && advancedSetter ? (
                                <button
                                    onClick={() => advancedSetter(!advancedVisible)}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all hover:brightness-110"
                                    style={{
                                        background: 'var(--sc-bg-overlay)',
                                        color: 'var(--sc-muted)',
                                        border: '1px solid var(--sc-border)',
                                    }}
                                >
                                    {advancedVisible ? 'Hide' : 'Show'} Advanced
                                    {advancedVisible
                                        ? <ChevronUpIcon className="size-3.5" />
                                        : <ChevronDownIcon className="size-3.5" />}
                                </button>
                            ) : null}
                        </div>
                    </div>
                    <CardContentOverlay
                        loading={pageCtx.isLoading}
                        error={pageCtx.swrError}
                    />
                </div>
            </div>
        </div>
    )
}
